import React, { useContext } from 'react';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';

import { Content, Wrapper } from './TaxonomyMapper.styles';
import { PermissionsContext } from 'contexts/PermissionsContext';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { ApiDataContext } from 'contexts/ApiDataContext';
import { CHART_COLORS } from 'styles/colors';
import {
	getApplyDataFromTree,
	getApTreeData,
	later,
	syncValuesFromTreeData,
	markMappedItems,
	mapOverTree,
	getTreeWrappedInRootNode,
} from './utils';
import { taxonomyMapperDataConverter } from 'utils/taxonomyMapperDataConverter';
import { SecondaryNav, SAVE_MESSAGE_TYPES } from './SecondaryNav';
import { IndirectTaxonomyMapper } from './IndirectTaxonomyMapper';
import { WarningDialog } from './WarningDialog';
import { RevertDialog } from './RevertDialog';
import {
	fetchWheelChartData,
	fetchIndirectTaxonomyMapperData,
	saveIndirectTaxonomyMapperData,
	applyTaxonomyIndirect,
	getAppState,
	saveAppliedIndirectTaxonomyMapperData,
	fetchAppliedIndirectTaxonomyMapperData,
} from 'utils/apiRequests';
import { getCurrencySymbolFromOptions } from 'Details/utils';
import { UPLOAD_STATUS } from 'Data/constants';
import { getCompanySpendItemsFromTree, hasTreeChanges } from './utils';
import { TAXONOMY_TREE_DATA_PARAMS as COMPANY_TAXONOMY_TREE_DATA_PARAMS } from './TaxonomyMapperContainer';
import ApplyTaxonomyMessage from 'Data/DownloadData/DownloadProgressBar';

const AP_TAXONOMY_TREE_DATA_PARAMS = {
	...COMPANY_TAXONOMY_TREE_DATA_PARAMS,
	dimensions: ['apcategory', 'apcategory', 'apcategory', 'apcategory'],
	filters: 'spendType',
	filterValues: 1,
};

export class IndirectTaxonomyMapperContainer extends React.Component {
	state = {
		undoHistory: [],
		appliedTreeData: [],
		apTreeData: null,
		projectTreeData: null,
		treeData: null,
		apSearch: '',
		projectSearch: '',
		isApplyWarningOpen: false,
		isRevertDialogOpen: false,
		skipUndoChange: false,
		isApplying: false,
	};

	componentDidMount() {
		this.fetchData();

		document.addEventListener('click', this.clearSearchValues, true);
	}

	componentWillUnmount() {
		document.removeEventListener('click', this.clearSearchValues);
		this.unmounted = true;
	}

	clearSearchValues = () => {
		const { apSearch, projectSearch } = this.state;

		if (apSearch || projectSearch) {
			this.setState({
				apSearch: '',
				projectSearch: '',
			});
		}
	};

	async fetchData() {
		await this.fetchTreeData();
		await this.fetchSavedProjectTreeData();
	}

	async fetchTreeData() {
		const { currency } = this.props;
		const apiRequestParams = { ...AP_TAXONOMY_TREE_DATA_PARAMS };

		if (currency) {
			apiRequestParams.currency = currency;
			apiRequestParams.currencyType = 'fixed';
		}

		const data = await fetchWheelChartData(apiRequestParams);

		let treeData = taxonomyMapperDataConverter.convert(data.data, true);

		this.setState({ treeData });

		return treeData;
	}

	async fetchSavedProjectTreeData() {
		const { taxonomyVersion, showMappedItems } = this.props;
		let { treeData, apTreeData } = this.state;

		let projectTreeData = await fetchIndirectTaxonomyMapperData(
			taxonomyVersion
		);
		let appliedTreeData = await fetchAppliedIndirectTaxonomyMapperData(
			taxonomyVersion
		);

		projectTreeData = syncValuesFromTreeData(projectTreeData, treeData);
		appliedTreeData = syncValuesFromTreeData(appliedTreeData, treeData);

		apTreeData = getApTreeData({
			treeData,
			projectTreeData,
			showMappedItems,
			prevTreeData: apTreeData,
			color: CHART_COLORS[0],
		});

		this.setState(
			{
				projectTreeData,
				appliedTreeData,
				skipUndoChange: true,
				apTreeData,
			},
			() => {
				this.setState({ skipUndoChange: false });
			}
		);
	}

	componentDidUpdate(prevProps, prevState) {
		const { showMappedItems } = this.props;
		let {
			treeData,
			apTreeData,
			projectTreeData,
			undoHistory,
			skipUndoChange,
		} = this.state;

		if (
			prevProps.showMappedItems !== showMappedItems ||
			prevState.treeData !== treeData
		) {
			apTreeData = getApTreeData({
				treeData,
				projectTreeData,
				showMappedItems,
				prevTreeData: apTreeData,
				color: CHART_COLORS[0],
			});

			this.setState({ apTreeData });
		}

		if (
			hasTreeChanges(projectTreeData, prevState.projectTreeData) &&
			!skipUndoChange
		) {
			this.setState({
				undoHistory: [...undoHistory, prevState.projectTreeData],
			});
			this.handleSave();
		}
	}

	handleChange = stateUpdates => {
		this.setState(stateUpdates);
	};

	handleUndo = () => {
		const { showMappedItems } = this.props;
		const { undoHistory, apTreeData, treeData } = this.state;
		const prevTreeData = undoHistory.slice(-1)[0];
		const remainingUndos = undoHistory.slice(0, -1);
		let stateUpdates = { projectTreeData: prevTreeData };

		stateUpdates = this.getApTreeDataStateUpdates(stateUpdates, {
			apTreeData,
			treeData,
			showMappedItems,
		});

		this.setState(
			{
				...stateUpdates,
				skipUndoChange: true,
				undoHistory: remainingUndos,
			},
			() => {
				this.setState({ skipUndoChange: false });
				this.handleSave();
			}
		);
	};

	handleRevert = () => {
		const { showMappedItems } = this.props;
		const { appliedTreeData, apTreeData, treeData } = this.state;
		let stateUpdates = { projectTreeData: appliedTreeData };

		stateUpdates = this.getApTreeDataStateUpdates(stateUpdates, {
			apTreeData,
			treeData,
			showMappedItems,
		});

		this.setState(stateUpdates);
		this.hideRevertDialog();
	};

	// TODO: move to utils
	getApTreeDataStateUpdates = (stateUpdates, state) => {
		stateUpdates = { ...stateUpdates };

		const { projectTreeData } = stateUpdates;
		const { apTreeData, showMappedItems, treeData } = state;

		if (showMappedItems) {
			stateUpdates.apTreeData = markMappedItems(apTreeData, projectTreeData);
		} else {
			// complete reset needed for the case when removing some of the projectTree items
			stateUpdates.apTreeData = getApTreeData({
				treeData,
				projectTreeData,
				showMappedItems,
				prevTreeData: apTreeData,
				color: CHART_COLORS[0],
			});
		}

		return stateUpdates;
	};

	getProjectTreeDataFromApTaxonomyTree(treeData) {
		// tree data levels need to be updated to match projectTreeData structure
		const updatedLevelsTree = mapOverTree(treeData, item => ({
			...item,
			level: item.level + 1,
		}));
		// and wrap into single project node
		const projectTreeData = getTreeWrappedInRootNode(updatedLevelsTree);

		return [projectTreeData];
	}

	handleApply = async () => {
		const { projectTreeData, treeData } = this.state;
		const { stateRefetch, taxonomyVersion, clearApiCache } = this.props;
		let applyData = getApplyDataFromTree(projectTreeData);

		if (applyData.length === 0) {
			const projectTreeData = this.getProjectTreeDataFromApTaxonomyTree(
				treeData
			);

			applyData = getApplyDataFromTree(projectTreeData);
		}

		// Refetch state provider on App level to check for blocking changes
		stateRefetch();

		try {
			const status = await getAppState();

			if (status.state !== UPLOAD_STATUS.CLASSIFYING) {
				this.setState({ isApplying: true });

				await applyTaxonomyIndirect(applyData, taxonomyVersion);
				await saveAppliedIndirectTaxonomyMapperData(
					projectTreeData,
					taxonomyVersion
				);
				stateRefetch();

				this.setState({
					isApplying: false,
					appliedTreeData: projectTreeData,
					showApplyMessage: true,
				});

				clearApiCache();

				setTimeout(this.handleCloseApplyMessage, 3000);
			} else {
				this.showApplyWarning();
			}
		} catch (e) {
			this.setState({
				isApplying: false,
				showApplyFailedMessage: true,
			});

			setTimeout(this.handleCloseApplyMessage, 3000);
		}
	};

	handleSave = async () => {
		const { taxonomyVersion } = this.props;
		const { projectTreeData } = this.state;

		try {
			this.setState({ saveMessageType: SAVE_MESSAGE_TYPES.inprogress });
			await Promise.all([
				later(1000),
				saveIndirectTaxonomyMapperData(projectTreeData, taxonomyVersion),
			]);
			this.setState({ saveMessageType: SAVE_MESSAGE_TYPES.success });
		} catch (e) {
			this.setState({ saveMessageType: SAVE_MESSAGE_TYPES.fail });
		}
	};

	handleShowMappedItemsChange = () => {
		const { setQueryStringStateAndPush, showMappedItems } = this.props;

		setQueryStringStateAndPush({
			showMappedItems: showMappedItems ? undefined : true,
		});
	};

	getCompaniesOptions = () => {
		const { projectTreeData, companiesOptions, treeData } = this.state;

		return companiesOptions.map(item => {
			const companyObj = treeData.find(({ name }) => name === item.value);
			const totalSpend = companyObj ? companyObj.value : 0;
			const mappedSpend = getCompanySpendItemsFromTree(
				projectTreeData,
				item.value
			).reduce((acc, item) => {
				return acc + item.value;
			}, 0);

			return {
				...item,
				spend: totalSpend,
				mappedSpend,
				percentage: Math.round((mappedSpend / totalSpend) * 100) || 0,
			};
		});
	};

	handleBackButtonClick = () => {
		const { history } = this.props;

		const prevPageUrl = this.getPrevPageUrl();

		history.push(prevPageUrl);
	};

	handleCloseApplyMessage = () => {
		if (this.unmounted) return;

		this.setState({ showApplyMessage: false, showApplyFailedMessage: false });
	};

	showApplyWarning = () => {
		this.setState({ isApplyWarningOpen: true });
	};

	hideApplyWarning = () => {
		this.setState({ isApplyWarningOpen: false });
	};

	showRevertDialog = () => {
		this.setState({ isRevertDialogOpen: true });
	};

	hideRevertDialog = () => {
		this.setState({ isRevertDialogOpen: false });
	};

	getPrevPageUrl() {
		const {
			location: { search },
		} = this.props;

		const params = new URLSearchParams(search);

		return params.get('prevPageUrl');
	}

	render() {
		const {
			permissions,
			showMappedItems,
			currencySymbol,
			history,
			location,
		} = this.props;
		const historyProps = { history, location };

		const {
			undoHistory,
			projectTreeData,
			apSearch,
			projectSearch,
			treeData,
			apTreeData,
			saveMessageType,
			isApplyWarningOpen,
			isRevertDialogOpen,
			appliedTreeData,
			isApplying,
			showApplyMessage,
			showApplyFailedMessage,
		} = this.state;

		if (!apTreeData || !projectTreeData) {
			return (
				<Wrapper>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</Wrapper>
			);
		}

		const hasApplyChanges = hasTreeChanges(projectTreeData, appliedTreeData);

		const isApplyDisabled = isApplying || !hasApplyChanges;
		const isRevertDisabled =
			!hasApplyChanges || (!appliedTreeData || appliedTreeData.length === 0);

		return (
			<React.Fragment>
				<Wrapper>
					<SecondaryNav
						permissions={permissions}
						showMappedItems={showMappedItems}
						onApply={this.handleApply}
						onUndo={this.handleUndo}
						onShowMappedItemsChange={this.handleShowMappedItemsChange}
						isUndoDisabled={undoHistory.length === 0}
						isApplyDisabled={isApplyDisabled}
						isRevertDisabled={isRevertDisabled}
						saveMessageType={saveMessageType}
						onRevert={this.showRevertDialog}
						prevPageUrl={this.getPrevPageUrl()}
						onBackButtonClick={this.handleBackButtonClick}
						isApplying={isApplying}
						canEdit={!permissions.includes('Taxonomy.Edit')}
						isIndirect
					/>
					{isApplyWarningOpen && (
						<WarningDialog onClose={this.hideApplyWarning} />
					)}
					{isRevertDialogOpen && (
						<RevertDialog
							onClose={this.hideRevertDialog}
							onSubmit={this.handleRevert}
						/>
					)}
					<Content>
						<IndirectTaxonomyMapper
							canEdit={!permissions.includes('Taxonomy.Edit')}
							spendType="indirect"
							currencySymbol={currencySymbol}
							apTreeData={apTreeData}
							treeData={treeData}
							projectTreeData={projectTreeData}
							apSearch={apSearch}
							projectSearch={projectSearch}
							onChange={this.handleChange}
							onShowMappedItemsChange={this.handleShowMappedItemsChange}
							showMappedItems={showMappedItems}
							getApTreeDataStateUpdates={this.getApTreeDataStateUpdates}
							{...historyProps}
						/>
					</Content>
				</Wrapper>

				{showApplyMessage && (
					<ApplyTaxonomyMessage
						successMessage="Taxonomy applied successfully"
						onClose={this.handleCloseApplyMessage}
					/>
				)}
				{showApplyFailedMessage && (
					<ApplyTaxonomyMessage
						failMessage="Failed to apply taxonomy"
						onClose={this.handleCloseApplyMessage}
						error
					/>
				)}
			</React.Fragment>
		);
	}
}

export default React.memo(props => {
	const { currency, showMappedItems, setQueryStringStateAndPush } = useContext(
		QueryStringContext
	);
	const { permissions } = useContext(PermissionsContext);
	const { clearApiCache } = useContext(ApiDataContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);
	const { refetch: stateRefetch } = useContext(UploadStateContext);
	const {
		settings: { taxonomyVersion },
	} = useContext(SpendCubeSettingsContext);

	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);

	return (
		<IndirectTaxonomyMapperContainer
			{...props}
			permissions={permissions || []}
			showMappedItems={!!showMappedItems}
			setQueryStringStateAndPush={setQueryStringStateAndPush}
			currencySymbol={currencySymbol}
			currency={currency}
			stateRefetch={stateRefetch}
			taxonomyVersion={taxonomyVersion}
			clearApiCache={clearApiCache}
		/>
	);
});
